import { isVisitor } from 'kits/authKit';
import IvicosStrings from 'kits/language/stringKit';
import { berlinDateString } from 'kits/berlinDateKit';
import { selector } from 'recoil';
import {
    aIdentityClaims,
    aUserStatus,
    aAtWorkLocation,
    aAtWorkCustomPhrase,
    aAwayStatusMessage,
    aFocusEndTime,
    aFocusDurationMinutes,
    aFocusTimeKnockDoor,
    aFocusTimeTextMessage,
    aOutOfOfficeReturnDate,
    aOutOfOfficeAbsenceComment,
    aOutOfOfficePreferredMethods,
    aOutOfOfficeReturnTime
} from 'shared-state/identity/atoms';
import { IProfile } from 'shared-state/identity/types';
import { aCurrentCampusId, aCurrentRoomId } from 'shared-state/location/atoms';
import type { IStatusSnapshot, IStoredStatusEmergencyContactBusy, IStoredStatusEmergencyContactOoo } from 'shared-state/identity/statusPersistence';

export const sProfile = selector<IProfile | undefined>({
    key: 'sProfile',
    get: ({ get }) => {
        const idClaims = get(aIdentityClaims);
        const room = get(aCurrentRoomId);
        const campusId = get(aCurrentCampusId);
        if (!idClaims || !campusId) return undefined;

        const nextProfile: IProfile = {
            uid: idClaims.uid,
            displayName: idClaims.displayName,
            profileImage: idClaims.profileImageURL,
            room: room,
            type: isVisitor(idClaims)
                ? 'visitor'
                : !isVisitor(idClaims) && idClaims.roles.includes('auto-confirmed-visitor')
                ? 'auto-confirmed-visitor'
                : !isVisitor(idClaims) && idClaims.roles.includes('unconfirmed_visitor')
                ? 'unconfirmed_visitor'
                : 'user',
            invitationId: idClaims.visitorInvId,
            campusId
        };

        return nextProfile;
    }
});

const VISITOR_ROLES = ['visitor', 'auto-confirmed-visitor', 'unconfirmed_visitor'];

/** The status feature (own status, inactivity rules, status sync) is for users and managers only, never for visitors. */
export const sCanUseStatus = selector<boolean>({
    key: 'sCanUseStatus',
    get: ({ get }) => {
        const identityClaims = get(aIdentityClaims);
        if (!identityClaims) return false;
        return !VISITOR_ROLES.some((role) => identityClaims.type === role || identityClaims.roles?.includes(role));
    }
});

/** Full status snapshot - status, phrase, location, duration, returnDate, absenceComment, emergencyContact. */
export const sStatusSnapshot = selector<IStatusSnapshot | null>({
    key: 'sStatusSnapshot',
    get: ({ get }) => {
        const identityClaims = get(aIdentityClaims);
        const userId = identityClaims?.uid;
        const userStatus = get(aUserStatus);
        if (!userId || !userStatus || !get(sCanUseStatus)) return null;

        const atWorkLocation = get(aAtWorkLocation);
        const atWorkCustomPhrase = get(aAtWorkCustomPhrase);
        const awayStatusMessage = get(aAwayStatusMessage);
        const focusEndTime = get(aFocusEndTime);
        const focusDurationMinutes = get(aFocusDurationMinutes);
        const focusTimeKnockDoor = get(aFocusTimeKnockDoor);
        const focusTimeTextMessage = get(aFocusTimeTextMessage);
        const outOfOfficeReturnDate = get(aOutOfOfficeReturnDate);
        const outOfOfficeReturnTime = get(aOutOfOfficeReturnTime);
        const outOfOfficeAbsenceComment = get(aOutOfOfficeAbsenceComment);
        const outOfOfficePreferredMethods = get(aOutOfOfficePreferredMethods);

        let phrase: string | null = null;
        let duration: number | null = null;
        let returnDateStr: string | null = null;
        let returnTimeStr: string | null = null;
        let absenceCommentStr: string | null = null;
        let emergencyContact: IStatusSnapshot['emergencyContact'] = null;

        if (userStatus === 'available') {
            phrase = atWorkLocation === 'custom' && atWorkCustomPhrase?.trim() ? atWorkCustomPhrase.trim() : IvicosStrings.statusAtWork;
        } else if (userStatus === 'away') {
            phrase = awayStatusMessage ?? null;
        } else if (userStatus === 'busy') {
            phrase = IvicosStrings.focusWorkUntilEnd;
            if (focusDurationMinutes != null && focusEndTime != null) {
                duration = focusDurationMinutes * 60 * 1000;
            }
            emergencyContact = { inviteToRoom: focusTimeKnockDoor, textMessage: focusTimeTextMessage } as IStoredStatusEmergencyContactBusy;
        } else if (userStatus === 'out-of-office') {
            phrase = outOfOfficeAbsenceComment?.trim() || null;
            absenceCommentStr = outOfOfficeAbsenceComment?.trim() || null;
            if (outOfOfficeReturnDate) {
                returnDateStr = berlinDateString(outOfOfficeReturnDate);
            }
            if (outOfOfficeReturnTime) {
                returnTimeStr = outOfOfficeReturnTime;
            }
            emergencyContact = outOfOfficePreferredMethods as IStoredStatusEmergencyContactOoo;
        } else if (userStatus === 'on-the-phone') {
            phrase = IvicosStrings.onPhoneUntilEnd;
            if (focusDurationMinutes != null && focusEndTime != null) {
                duration = focusDurationMinutes * 60 * 1000;
            }
        }

        const endTime = userStatus === 'busy' || userStatus === 'on-the-phone' ? focusEndTime ?? null : null;
        const durationMinutes = userStatus === 'busy' || userStatus === 'on-the-phone' ? focusDurationMinutes ?? null : null;

        return {
            userId,
            status: userStatus,
            location: userStatus === 'available' ? atWorkLocation : null,
            phrase,
            duration,
            endTime,
            durationMinutes,
            returnDate: returnDateStr,
            returnTime: returnTimeStr,
            absenceComment: absenceCommentStr,
            emergencyContact
        };
    }
});
